import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Input,
  Popover,
  Text,
} from "@chakra-ui/react";
import { DateObj, useDayzed } from "dayzed";
import { Calendar, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useState } from "react";
import { formatDate } from "../helpers/time";

const monthNames = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const weekDays = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

type Props = {
  setValue: (date: string | undefined) => void;
  value?: string;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  isDisabled?: boolean;
};

const DatePicker = ({
  placeholder = "Seleccionar fecha",
  setValue,
  value,
  minDate,
  maxDate,
  isDisabled = false,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleDateSelect = ({ date }: DateObj) => {
    setValue(formatDate(date));
    setIsOpen(false);
  };

  const handleOpenChange = (details: { open: boolean }) => {
    setIsOpen(details.open);
  };

  const { calendars, getBackProps, getForwardProps, getDateProps } = useDayzed({
    selected: value ? [new Date(`${value}T00:00:00`)] : [],
    onDateSelected: handleDateSelect,
    minDate,
    maxDate,
  });

  const calendar = calendars[0];

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Box width="100%">
        <Popover.Trigger asChild>
          <Box position="relative">
            <Input
              size="sm"
              placeholder={placeholder}
              disabled={isDisabled}
              _placeholder={{ color: "#5D5C60" }}
              readOnly
              cursor="pointer"
              pl={4}
              pr={10}
              value={value ?? ""}
            />
            <Box
              position="absolute"
              right={3}
              top="50%"
              transform="translateY(-50%)"
              zIndex={1}
              cursor="pointer"
              onClick={(e) => {
                e.stopPropagation();
                if (!isDisabled) {
                  if (value) {
                    setValue(undefined);
                    setIsOpen(false);
                  } else {
                    setIsOpen(true);
                  }
                }
              }}
            >
              {value ? <X size={16} /> : <Calendar size={16} />}
            </Box>
          </Box>
        </Popover.Trigger>

        <Popover.Positioner>
          <Popover.Content minW="280px" width="auto" p={2}>
            <Flex justify="space-between" align="center" mb={4}>
              <Button
                {...getBackProps({ calendars })}
                variant="ghost"
                size="sm"
              >
                <ChevronLeft />
              </Button>
              <Text fontWeight="medium">
                {monthNames[calendar.month]} {calendar.year}
              </Text>
              <Button
                {...getForwardProps({ calendars })}
                variant="ghost"
                size="sm"
              >
                <ChevronRight />
              </Button>
            </Flex>
            <Grid templateColumns="repeat(7, 1fr)" gap={1}>
              {weekDays.map((day) => (
                <GridItem
                  key={day}
                  textAlign="center"
                  fontWeight="medium"
                  fontSize="sm"
                >
                  {day}
                </GridItem>
              ))}
              {calendar.weeks.map((week, weekIdx) =>
                week.map((dateObj, idx) => {
                  if (typeof dateObj === "string") {
                    return <GridItem key={`${weekIdx}-${idx}`} />;
                  }

                  const { date, selected, selectable, today } = dateObj;

                  return (
                    <GridItem key={`${weekIdx}-${idx}`}>
                      <Button
                        {...getDateProps({ dateObj })}
                        size="sm"
                        width="100%"
                        height="32px"
                        variant={selected ? "solid" : "ghost"}
                        bg={
                          selected
                            ? "merchants.primary.app"
                            : today
                            ? "gray.100"
                            : undefined
                        }
                        color={selected ? "white" : today ? "black" : undefined}
                        _hover={{
                          bg: "gray.400",
                        }}
                        borderRadius="md"
                        disabled={!selectable}
                      >
                        {date.getDate()}
                      </Button>
                    </GridItem>
                  );
                })
              )}
            </Grid>
          </Popover.Content>
        </Popover.Positioner>
      </Box>
    </Popover.Root>
  );
};

export default DatePicker;
